import {
  Button,
  LoadError,
  Modal,
  ModalActions,
  PanelTable,
  type PanelTableColumn,
  SkeletonRows,
} from '~/components/ui';
import type { AgentSummary } from '~/modules/query/ai/types';

export interface AgentPickerModalProps {
  agents: AgentSummary[];
  selectedAgentId: string;
  isLoading: boolean;
  errorMessage?: string;
  onRetry: () => void;
  onClose: () => void;
  onSelect: (agent: AgentSummary) => void;
}

/**
 * Picks the assistant's agent from the shared agent catalog. A picker, not an
 * editor: the row's single action writes the form and the page closes the modal.
 */
export default function AgentPickerModal({
  agents,
  selectedAgentId,
  isLoading,
  errorMessage,
  onRetry,
  onClose,
  onSelect,
}: AgentPickerModalProps) {
  const columns: PanelTableColumn<AgentSummary>[] = [
    {
      header: 'Name',
      width: 'w-56',
      render: (agent) => (
        <span className="font-medium text-gray-800 dark:text-white/90">{agent.name}</span>
      ),
    },
    {
      header: 'Key',
      width: 'w-56',
      render: (agent) => (
        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{agent.agentKey}</span>
      ),
    },
    {
      header: 'Description',
      render: (agent) =>
        agent.description || <span className="text-gray-400 dark:text-gray-500">—</span>,
    },
    {
      header: 'Actions',
      width: 'w-28',
      render: (agent) => (
        <Button
          variant={agent.id === selectedAgentId ? 'primary' : 'outline'}
          size="sm"
          onClick={() => onSelect(agent)}
          testId={`ai-digital-assistants-agent-pick-${agent.agentKey}`}
        >
          {agent.id === selectedAgentId ? 'Selected' : 'Select'}
        </Button>
      ),
    },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Select agent"
      description="The agent supplies the assistant's behavior. Its instructions are not edited here."
      testId="ai-digital-assistants-agent-picker"
      footer={
        <ModalActions
          onDismiss={onClose}
          dismissLabel="Cancel"
          dismissTestId="ai-digital-assistants-agent-picker-cancel"
        />
      }
    >
      {isLoading ? (
        <SkeletonRows />
      ) : errorMessage ? (
        <LoadError
          what="agents"
          message={errorMessage}
          onRetry={onRetry}
          testId="ai-digital-assistants-agent"
        />
      ) : (
        <PanelTable<AgentSummary>
          ariaLabel="Agents"
          testId="ai-digital-assistants-agent-table"
          columns={columns}
          data={agents}
          getRowId={(agent) => agent.id}
          maxHeight="50vh"
          emptyMessage="No agents are available."
          emptyDescription="Ask an administrator to add one."
        />
      )}
    </Modal>
  );
}
